// @flow
import {
  emailValidation,
  phoneNumber
} from '../../../../utilities/validations';
import { SubmissionError } from 'redux-form';
import { type Values } from './handleSubmit';

type Errors = {
  user_attributes: {
    first_name?: string,
    last_name?: string,
    email?: string,
    phone_number?: string
  },
  company_detail_attributes: {
    company_name?: string,
    address_attributes: {
      address?: string,
      city?: string,
      state?: string,
      zip?: string,
      country_code?: string
    }
  },
  user_currency?: string
};
export const getCompanyDetailsErrors = (values: Values) => {
  const errors: Errors = {
    user_attributes: {},
    company_detail_attributes: { address_attributes: {} }
  };
  const { company_detail_attributes, user } = values;
  if (!(user || {}).first_name) {
    errors.user_attributes.first_name = 'This field is required';
  }
  if (!(user || {}).last_name) {
    errors.user_attributes.last_name = 'This field is required';
  }
  if (!(user || {}).email) {
    errors.user_attributes.email = 'This field is required';
  }
  if (!(user || {}).phone_number) {
    errors.user_attributes.phone_number = 'This field is required';
  }
  if (!(company_detail_attributes || {}).company_name) {
    errors.company_detail_attributes.company_name = 'This field is required';
  }
  if (!((company_detail_attributes || {}).address_attributes || {}).address) {
    errors.company_detail_attributes.address_attributes.address =
      'This field is required';
  }
  if (!((company_detail_attributes || {}).address_attributes || {}).city) {
    errors.company_detail_attributes.address_attributes.city =
      'This field is required';
  }
  if (!((company_detail_attributes || {}).address_attributes || {}).state) {
    errors.company_detail_attributes.address_attributes.state =
      'This field is required';
  }
  if (!((company_detail_attributes || {}).address_attributes || {}).zip) {
    errors.company_detail_attributes.address_attributes.zip =
      'This field is required';
  }
  if (
    !((company_detail_attributes || {}).address_attributes || {}).country_code
  ) {
    errors.company_detail_attributes.address_attributes.country_code =
      'This field is required';
  }
  if (!values.user_currency) {
    errors.user_currency = 'This field is required';
  }
  return errors;
};

export const checkCompanyDetailsErrors = (errors: Errors) =>
  Object.keys(errors).length > 2 ||
  Object.keys(errors.user_attributes).length > 0 ||
  Object.keys(errors.company_detail_attributes).length > 1 ||
  Object.keys(errors.company_detail_attributes.address_attributes).length > 0;

export const validateCompanyDetails = (values: Values) => {
  const errors = getCompanyDetailsErrors(values);
  if (checkCompanyDetailsErrors(errors)) {
    throw new SubmissionError(errors);
  }
};

export const getAboutErrors = (values: Values) => {
  const details = values.company_detail_attributes || {};
  const companyErrors = {};
  const errors = {};

  if (!details.description) {
    companyErrors.description = 'This field is required';
  }
  if (details.description && details.description.length > 255) {
    companyErrors.description = 'Please enter a maximum of 255 characters.';
  }

  if (!(details.website || details.no_website)) {
    companyErrors.website = 'This field is required';
  }
  if (!details.type_of_company) {
    companyErrors.type_of_company = 'This field is required';
  }
  // if (!values.certificate) {
  //   errors.certificate = 'This field is required';
  // }
  // if (!values.article && details.type_of_company === 'Corporation') {
  //   errors.article = 'This field is required';
  // }
  const { certificate, article } = values;
  if (
    certificate &&
    article &&
    typeof certificate !== 'boolean' &&
    typeof article !== 'boolean' &&
    certificate.name === article.name &&
    certificate.size === article.size
  ) {
    // errors.certificate = 'This field is required';
    errors.article = 'Please upload a unique Proof of Ownership document';
  }
  errors.company_detail_attributes = companyErrors;
  return errors;
};

export const checkAboutErrors = (errors: Errors) =>
  Object.keys(errors).length > 1 ||
  Object.keys(errors.company_detail_attributes).length !== 0;

export const validateAbout = (values: Values) => {
  const errors = getAboutErrors(values);
  if (checkAboutErrors(errors)) {
    throw new SubmissionError(errors);
  }
};

export const getMailingErrors = (values: Values) => {
  const details =
    (values.mailing_address_attributes || {}).address_attributes || {};
  const errors = {};

  if (typeof values.diff_mailing_address === 'undefined') {
    return { diff_mailing_address: 'This field is required' };
  }
  if (values.diff_mailing_address === 'no') {
    return {};
  }

  if (!details.address) {
    errors.address = 'This field is required';
  }
  if (!details.city) {
    errors.city = 'This field is required';
  }
  if (!details.state) {
    errors.state = 'This field is required';
  }
  if (!details.zip) {
    errors.zip = 'This field is required';
  }
  if (!details.country_code) {
    errors.country_code = 'This field is required';
  }
  return errors;
};

export const checkMailingErrors = (errors: Errors) =>
  Object.keys(errors).length !== 0;

export const validateMailing = (values: Values) => {
  const errors = getMailingErrors(values);
  if (Object.keys(errors).length) {
    throw new SubmissionError({
      mailing_address_attributes: { address_attributes: errors }
    });
  }
};

export const getContactErrors = (values: Values) => {
  const details = values.company_contact_attributes || {};
  const errors = {};

  if (!details.administrative_name) {
    errors.administrative_name = 'This field is required';
  }
  if (!details.administrative_phone) {
    errors.administrative_phone = 'This field is required';
  } else {
    const phoneError = phoneNumber(details.administrative_phone);
    if (phoneError) {
      errors.administrative_phone = phoneError;
    }
  }
  if (!details.administrative_email) {
    errors.administrative_email = 'This field is required';
  } else {
    const emailError = emailValidation(details.administrative_email);
    if (emailError) {
      errors.administrative_email = emailError;
    }
  }
  if (details.technical_email) {
    const emailError = emailValidation(details.technical_email);
    if (emailError) {
      errors.technical_email = emailError;
    }
  }
  return errors;
};

export const checkContactErrors = (errors: Errors) =>
  Object.keys(errors).length !== 0;

const joinAsSentence = (words: string[]) => {
  if (words.length < 2) {
    return words[0] || '';
  } else {
    return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
  }
};

export const validateContact = (values: Values) => {
  const companyDetailsErrors = checkCompanyDetailsErrors(
    getCompanyDetailsErrors(values)
  );
  // $FlowFixMe
  const mailingErrors = checkMailingErrors(getMailingErrors(values));
  const aboutErrors = checkAboutErrors(getAboutErrors(values));
  const errorSections = [];
  if (companyDetailsErrors) {
    errorSections.push('Company Details');
  }
  if (mailingErrors) {
    errorSections.push('Mailing Address');
  }
  if (aboutErrors) {
    errorSections.push('About The Company');
  }
  let _error;
  if (errorSections.length > 0) {
    _error = `Please complete ${joinAsSentence(errorSections)} section${
      errorSections.length > 1 ? 's' : ''
    }`;
  }

  const errors = getContactErrors(values);
  if (Object.keys(errors).length || _error) {
    throw new SubmissionError({
      _error,
      company_contact_attributes: errors
    });
  }
};
